import type { Resource } from '../../types/resource';
import { useResourceViewerStore } from '../../stores/resourceViewerStore';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ReadonlyInput({ value }: { value: string }) {
  return <input type="text" defaultValue={value} className="form-control" readOnly />;
}

function EmailList({ emails }: { emails: string[] }) {
  return (
    <table className="table table-hover table-condensed table-iio table-lookups">
      <tbody>
        {emails.map((email, i) => <tr key={i}><td>{email}</td></tr>)}
      </tbody>
    </table>
  );
}

export default function MetadataTab({ resource }: { resource: Resource }) {
  const close = useResourceViewerStore(s => s.close);
  const hasUserMeta = resource.userMetadata && Object.keys(resource.userMetadata).length > 0;
  const hasExtMeta = resource.metadata && Object.keys(resource.metadata).length > 0;

  return (
    <div style={{ overflowY: 'auto', maxHeight: '100%', padding: '10px' }}>
      <h4>Core Metadata</h4>
      <table className="table table-hover table-condensed table-iio" data-testid="viewer-core-metadata-table">
        <colgroup><col style={{ width: 200 }} /><col /></colgroup>
        <thead><tr><th>Key</th><th>Value</th></tr></thead>
        <tbody>
          <tr><td>Folio Identifier</td><td>{resource.id}</td></tr>
          <tr><td>Filename</td><td>{resource.filename}</td></tr>
          <tr><td>MimeType</td><td>{resource.mimeType}</td></tr>
          <tr><td>Size</td><td>{formatBytes(resource.bytes)}</td></tr>
          {resource.isContainer && <tr><td>Type</td><td>Container</td></tr>}
          {resource.isRegularFile && <tr><td>Type</td><td>Regular File</td></tr>}
          {resource.type && <tr><td>Resource Type</td><td>{resource.type}</td></tr>}
          {resource.createdDate && <tr><td>Created Date</td><td>{resource.createdDate}</td></tr>}
          {resource.modifiedDate && <tr><td>Modified Date</td><td>{resource.modifiedDate}</td></tr>}
          {resource.title && <tr><td>Title</td><td>{resource.title}</td></tr>}
          {resource.creator && <tr><td>Creator</td><td>{resource.creator}</td></tr>}
          {resource.emailFrom && <tr><td>From</td><td><EmailList emails={[resource.emailFrom]} /></td></tr>}
          {resource.emailTo?.length > 0 && <tr><td>To</td><td><EmailList emails={resource.emailTo} /></td></tr>}
          {resource.emailCc?.length > 0 && <tr><td>Cc</td><td><EmailList emails={resource.emailCc} /></td></tr>}
          {resource.emailBcc?.length > 0 && <tr><td>Bcc</td><td><EmailList emails={resource.emailBcc} /></td></tr>}
          {resource.emailRecipients?.length > 0 && <tr><td>Recipients</td><td><EmailList emails={resource.emailRecipients} /></td></tr>}
          {resource.emailConversationTopic && <tr><td>EmailConversationTopic</td><td>{resource.emailConversationTopic}</td></tr>}
          {resource.md5 && <tr><td>MD5</td><td><ReadonlyInput value={resource.md5} /></td></tr>}
          {resource.sha1 && <tr><td>SHA1</td><td><ReadonlyInput value={resource.sha1} /></td></tr>}
          {resource.sha256 && <tr><td>SHA256</td><td><ReadonlyInput value={resource.sha256} /></td></tr>}
          {resource.path && <tr><td>Path</td><td><ReadonlyInput value={resource.path} /></td></tr>}
          <tr><td>Collection</td><td><a href={`#!/project/${resource.projectId}/collection/${resource.collectionId}`} onClick={close} data-testid="viewer-collection-link">{resource.collectionName ?? resource.collectionId}</a></td></tr>
        </tbody>
      </table>

      {resource.resourceBates.length > 0 && (
        <>
          <h4>Bates</h4>
          {resource.resourceBates.map((b, i) => (
            <table key={i} className="table table-hover table-condensed table-iio" data-testid="viewer-bates-table">
              <colgroup><col style={{ width: 200 }} /><col /></colgroup>
              <thead>
                <tr>
                  <th colSpan={2}>
                    {b.exportId && <><i className="fas fa-share" />{' '}</>}
                    {b.importId && <><i className="fas fa-retweet" />{' '}</>}
                    {b.name} <small className="text-muted">{b.createdAt}</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Beg Bates</td><td>{b.begBates}</td></tr>
                <tr><td>End Bates</td><td>{b.endBates}</td></tr>
                <tr><td>Beg Attach Bates</td><td>{b.begAttachBates}</td></tr>
                <tr><td>End Attach Bates</td><td>{b.endAttachBates}</td></tr>
              </tbody>
            </table>
          ))}
        </>
      )}

      {hasUserMeta && (
        <>
          <h4>User Metadata</h4>
          <table className="table table-hover table-condensed table-iio" data-testid="viewer-user-metadata-table">
            <colgroup><col style={{ width: 200 }} /><col /></colgroup>
            <thead><tr><th>Key</th><th>Value</th></tr></thead>
            <tbody>
              {Object.entries(resource.userMetadata).map(([k, v]) => (
                <tr key={k}><td>{k}</td><td>{String(v)}</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h4>Extended Metadata</h4>
      {!hasExtMeta ? (
        <div className="alert alert-info alert-block">
          <h4>No extended metadata found</h4>
          <p>This document did not produce any extended metadata</p>
        </div>
      ) : (
        <table className="table table-hover table-condensed table-iio" data-testid="viewer-extended-metadata-table">
          <colgroup><col style={{ width: 200 }} /><col /></colgroup>
          <thead><tr><th>Key</th><th>Value</th></tr></thead>
          <tbody>
            {Object.entries(resource.metadata).map(([k, v]) => (
              <tr key={k}><td>{k}</td><td>{String(v)}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
